import { useMemo } from 'react';

/**
 * Render Claude-generated SVG markup safely inline.
 *
 * The AI is taught (in the troubleshoot system prompt) to emit small
 * schematics inside [svg]...[/svg] markers — meter probe placement,
 * terminal layouts, etc. We sanitize defensively before injecting:
 *
 *   - Strip <script>, <foreignObject>, <iframe>, <image>, <animate*>
 *     (anything that can fetch external resources or run code).
 *   - Strip on* event-handler attributes.
 *   - Strip javascript: URIs in href / xlink:href.
 *   - Strip external xlink:href / href (only fragment refs allowed,
 *     so <use href="#thing"> still works for internal symbol reuse).
 *
 * If sanitization fails or the markup parses to nothing, we render
 * a tiny fallback tile so the chat layout doesn't shift.
 */

const FORBIDDEN_TAGS = new Set([
  'script',
  'foreignobject',
  'iframe',
  'image',          // <image> can fetch external bitmaps
  'animation',
  'animate',
  'animatemotion',
  'animatetransform',
  'set',
  'handler',
]);

function sanitizeSvgString(raw) {
  if (!raw || typeof raw !== 'string') return null;
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(raw, 'image/svg+xml');
    // Reject parser errors — DOMParser yields a <parsererror> element on bad input.
    if (doc.querySelector('parsererror')) return null;

    const svg = doc.querySelector('svg');
    if (!svg) return null;

    cleanElement(svg);

    return new XMLSerializer().serializeToString(svg);
  } catch (err) {
    console.warn('InlineSvg sanitize failed:', err);
    return null;
  }
}

function cleanElement(el) {
  const tag = el.tagName.toLowerCase();
  if (FORBIDDEN_TAGS.has(tag)) {
    el.remove();
    return;
  }

  // Pass over attributes (snapshot first because we mutate)
  for (const attr of Array.from(el.attributes)) {
    const name = attr.name.toLowerCase();
    const value = attr.value;

    if (name.startsWith('on')) {
      el.removeAttribute(attr.name);
      continue;
    }
    if ((name === 'href' || name === 'xlink:href') && /^\s*javascript:/i.test(value)) {
      el.removeAttribute(attr.name);
      continue;
    }
    // Disallow external href / xlink:href — only same-doc fragment refs.
    if ((name === 'xlink:href' || name === 'href') && value && !value.startsWith('#')) {
      el.removeAttribute(attr.name);
      continue;
    }
    if (name === 'style' && /expression\s*\(|url\s*\(\s*['"]?\s*javascript:/i.test(value)) {
      el.removeAttribute(attr.name);
      continue;
    }
  }

  // Recurse (snapshot — children may be removed)
  for (const child of Array.from(el.children)) {
    cleanElement(child);
  }
}

export default function InlineSvg({ markup, className = '' }) {
  const sanitized = useMemo(() => sanitizeSvgString(markup), [markup]);

  if (!sanitized) {
    return (
      <div
        className={`bg-ink-800 border border-ink-700 rounded-lg px-3 py-2 text-xs text-ink-400 inline-flex items-center gap-2 ${className}`}
      >
        <span className="text-ink-400">⌧</span> Schematic unavailable
      </div>
    );
  }

  return (
    <div
      className={`bg-ink-950 border border-ink-700 rounded-lg p-3 inline-block ${className}`}
      // Sanitized above — every dangerous element/attribute has been stripped.
      // We deliberately use innerHTML so the SVG renders as real elements
      // (innerText/textContent would just print the markup as a string).
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  );
}
